// src/utils/noteHighlighter.js
import * as Tone from 'tone';

export class NoteHighlighter {
    constructor(osmd) {
      this.osmd = osmd;
      this.highlightedNotes = new Map(); // Map noteId to original colors
      this.currentHighlights = new Set(); // Track currently highlighted note IDs
      this.lookaheadHighlights = new Set(); // Track currently highlighted lookahead note IDs
      this.partVolumeGetter = null; // Function to get part volume
      this.mutedPartsRef = null; // Reference to muted parts
      this.lastHighlightedNotes = ''; // Track changes to reduce spam
      this.partControlsGetter = null; // Function to get current part controls
      this.partToInstrumentMapRef = null; // Reference to part-to-instrument mapping
    }

    // Set references to audio system for volume/mute status
    setAudioReferences(getPartVolume, mutedPartsRef) {
      this.partVolumeGetter = getPartVolume;
      this.mutedPartsRef = mutedPartsRef;
    }

    // Set references to part controls and mapping for divisi highlighting
    setPartControlReferences(getPartControls, partToInstrumentMapRef) {
      this.partControlsGetter = getPartControls;
      this.partToInstrumentMapRef = partToInstrumentMapRef;
    }

    // Get current part controls
    getCurrentPartControls() {
      if (!this.partControlsGetter) {
        return [];
      }
      const result = this.partControlsGetter();
      return Array.isArray(result) ? result : [];
    }
  
    // Find the graphical note that corresponds to a specific source note
    findGraphicalNoteForSourceNote(sourceNote) {
      if (!this.osmd?.graphic?.measureList || !sourceNote) {
        return null;
      }
      
      try {
        // Iterate through all measures
        for (let measureIndex = 0; measureIndex < this.osmd.graphic.measureList.length; measureIndex++) {
          const measureList = this.osmd.graphic.measureList[measureIndex];
          
          for (let staffIndex = 0; staffIndex < measureList.length; staffIndex++) {
            const measure = measureList[staffIndex];
            
            // Iterate through staff entries in this measure
            for (const staffEntry of measure.staffEntries) {
              // Iterate through voice entries
              for (const voiceEntry of staffEntry.graphicalVoiceEntries) {
                for (const graphicalNote of voiceEntry.notes) {
                  // Check if this is the exact same source note object
                  if (graphicalNote.sourceNote === sourceNote) {
                    return graphicalNote;
                  }
                }
              }
            }
          }
        }
      } catch (error) {
      }
      
      return null;
    }
  
    // Highlight a chord (multiple notes in same SVG container) with voice filtering
    highlightChord(graphicalNote, svgElement, noteEvents) {
      if (!graphicalNote || !noteEvents || noteEvents.length === 0) {
        return false;
      }
      
      try {
        const noteId = graphicalNote.getSVGId() || `chord_${Date.now()}_${Math.random()}`;
        
        // Skip if already highlighted
        if (this.currentHighlights.has(noteId)) {
          return true;
        }
        
        // Store original colors if not already stored
        if (!this.highlightedNotes.has(noteId)) {
          this.storeOriginalColors(svgElement, noteId);
        }
        
        // Find noteheads in the chord
        const noteheadElements = this.findNoteheads(svgElement);
        const stemElements = this.findStemElements(svgElement);
        // console.log(`🎵 CHORD: Found ${noteheadElements.length} noteheads for ${noteEvents.map(n => n.pitch).join(' + ')}`);
        
        if (noteheadElements.length >= 2) {
          // Get part control using the first note (all notes in chord should have same part)
          const firstNote = noteEvents[0];
          let partControl = null;
          
          if (this.partToInstrumentMapRef && this.partToInstrumentMapRef.current) {
            const instrumentId = this.partToInstrumentMapRef.current[firstNote.partId];
            partControl = this.getCurrentPartControls().find(part => part.id.toString() === instrumentId?.toString());
          }
          
          if (!partControl) {
            partControl = this.getCurrentPartControls().find(part => part.id.toString() === firstNote.partId.toString());
          }
          
          if (partControl) {
            // Sort noteheads by Y position (same as simple-svg-explorer2)
            const sortedNoteheads = [...noteheadElements].sort((a, b) => a.y - b.y);
            
            // Apply voice logic: highlight all noteheads but hide disabled voices (like simple-svg-explorer2)
            const midIndex = Math.floor(sortedNoteheads.length / 2);
            const highlightColor = this.getHighlightColor(firstNote);
            
            let allVoicesShown = true;
            sortedNoteheads.forEach((notehead, sortedIndex) => {
              // Upper voice = smaller Y values (higher pitches) = first half
              const isUpperVoice = sortedIndex < midIndex || (sortedNoteheads.length === 2 && sortedIndex === 0);

              // Lower voice = larger Y values (lower pitches) = second half
              const isLowerVoice = sortedIndex >= midIndex || (sortedNoteheads.length === 2 && sortedIndex === 1);

              let shouldShow = false;
              if (isUpperVoice && partControl.upperVoice !== false) {
                shouldShow = true;
              } else if (isLowerVoice && partControl.lowerVoice !== false) {
                shouldShow = true;
              }

              // Always keep noteheads visible - never hide them
              notehead.element.style.display = '';

              if (shouldShow) {
                // Highlight this notehead (enabled voice)
                this.applyColorToElements([notehead.element], highlightColor);
              } else {
                allVoicesShown = false;
                // Don't highlight, but keep visible (disabled voice stays black)
                // Reset to original colors to ensure it's black
                notehead.element.style.fill = '#000000';
                notehead.element.style.stroke = '#000000';
                notehead.element.style.filter = 'none';
                notehead.element.style.strokeWidth = '';
              }
            });

            // The stem is shared by every notehead on it, so only color it
            // when all of them are highlighted
            if (allVoicesShown) {
              this.applyColorToElements(stemElements, highlightColor);
            }
          } else {
            // No part control - highlight all noteheads and their shared stem
            const highlightColor = this.getHighlightColor(firstNote);
            this.applyColorToElements([...noteheadElements.map(nh => nh.element), ...stemElements], highlightColor);
          }
        } else {
          // Single notehead - the stem belongs to it alone, so highlight both
          const firstNote = noteEvents[0];
          const highlightColor = this.getHighlightColor(firstNote);
          this.applyColorToElements([noteheadElements[0]?.element, ...stemElements].filter(Boolean), highlightColor);
        }
        
        this.currentHighlights.add(noteId);
        return true;
        
      } catch (error) {
        console.error('Error highlighting chord:', error);
        return false;
      }
    }

    // Highlight a graphical note with appropriate color based on voice status
    highlightNote(graphicalNote, noteEvent = null) {
      if (!graphicalNote) {
        return false;
      }
      
      try {
        const svgElement = graphicalNote.getSVGGElement();
        if (!svgElement) {
          return false;
        }
  
        const noteId = graphicalNote.getSVGId() || `note_${Date.now()}_${Math.random()}`;
        
        
        // Skip if already highlighted
        if (this.currentHighlights.has(noteId)) {
          return true;
        }
        
        // Store original colors if not already stored
        if (!this.highlightedNotes.has(noteId)) {
          this.storeOriginalColors(svgElement, noteId);
        }
        
        // Determine highlight color based on voice status
        const highlightColor = this.getHighlightColor(noteEvent);
        
        // Apply appropriate color to all note elements
        this.applyColor(svgElement, highlightColor, noteEvent);
        
        this.currentHighlights.add(noteId);
        return true;
        
      } catch (error) {
        return false;
      }
    }

    // Determine the appropriate highlight color based on voice/part status
    getHighlightColor(noteEvent) {
      if (!noteEvent || !this.partVolumeGetter || !this.mutedPartsRef) {
        // console.log('NoteHighlighter: Missing noteEvent or audio references, defaulting to playing');
        return 'playing'; // Default to playing color if no audio info available
      }

      const partId = noteEvent.partId;
      const partIdStr = String(partId);
      
      // Debug: Log note event properties
      if (noteEvent.isSwung) {
        // console.log(`NoteHighlighter: Found SWUNG note! partId=${partId}, time=${noteEvent.startTime?.toFixed(3) || noteEvent.time?.toFixed(3)}, swingReason=${noteEvent.swingReason}`);
      }
      
      // Check if part is muted
      const isMuted = this.mutedPartsRef.current.has(partIdStr) || this.mutedPartsRef.current.has(partId);
      
      // Check if part volume is 0
      const partVolume = this.partVolumeGetter(partId);
      const isVolumeZero = partVolume === 0;
      
      if (isMuted || isVolumeZero) {
        return 'muted';
      }
      
      // Check if note is swung
      if (noteEvent.isSwung) {
        // console.log('NoteHighlighter: Returning SWUNG color for note');
        return 'swung';
      }
      
      return 'playing';
    }

    // Get highlight color based on context (current vs lookahead)
    getHighlightColorWithContext(noteEvent, highlightContext) {
      if (highlightContext === 'lookahead') {
        return 'lookahead';
      }
      // For current notes, use the standard color logic
      return this.getHighlightColor(noteEvent);
    }

    // Highlight a note with context (current vs lookahead)
    highlightNoteWithContext(graphicalNote, noteEvent, highlightContext) {
      if (!graphicalNote) {
        return false;
      }
      
      try {
        const svgElement = graphicalNote.getSVGGElement();
        if (!svgElement) {
          return false;
        }

        const noteId = graphicalNote.getSVGId() || `note_${Date.now()}_${Math.random()}`;
        
        // Skip if already highlighted
        if (this.currentHighlights.has(noteId)) {
          return true;
        }
        
        // Store original colors if not already stored
        if (!this.highlightedNotes.has(noteId)) {
          this.storeOriginalColors(svgElement, noteId);
        }
        
        // Determine highlight color based on context
        const highlightColor = this.getHighlightColorWithContext(noteEvent, highlightContext);
        
        // Apply appropriate color to all note elements
        this.applyColor(svgElement, highlightColor, noteEvent);
        
        this.currentHighlights.add(noteId);
        if (highlightContext === 'lookahead') {
          this.lookaheadHighlights.add(noteId);
        }
        return true;
        
      } catch (error) {
        return false;
      }
    }

    // Highlight a chord with context (current vs lookahead)
    highlightChordWithContext(graphicalNote, svgElement, noteEvents, highlightContext) {
      if (!graphicalNote || !noteEvents || noteEvents.length === 0) {
        return false;
      }
      
      try {
        const noteId = graphicalNote.getSVGId() || `chord_${Date.now()}_${Math.random()}`;
        
        // Skip if already highlighted
        if (this.currentHighlights.has(noteId)) {
          return true;
        }
        
        // Store original colors if not already stored
        if (!this.highlightedNotes.has(noteId)) {
          this.storeOriginalColors(svgElement, noteId);
        }
        
        // Find noteheads in the chord
        const noteheadElements = this.findNoteheads(svgElement);
        const stemElements = this.findStemElements(svgElement);

        if (noteheadElements.length >= 2) {
          // Get part control using the first note (all notes in chord should have same part)
          const firstNote = noteEvents[0];
          let partControl = null;
          
          if (this.partToInstrumentMapRef && this.partToInstrumentMapRef.current) {
            const instrumentId = this.partToInstrumentMapRef.current[firstNote.partId];
            partControl = this.getCurrentPartControls().find(part => part.id.toString() === instrumentId?.toString());
          }
          
          if (!partControl) {
            partControl = this.getCurrentPartControls().find(part => part.id.toString() === firstNote.partId.toString());
          }
          
          if (partControl) {
            // Sort noteheads by Y position
            const sortedNoteheads = [...noteheadElements].sort((a, b) => a.y - b.y);
            
            // Apply voice logic
            const midIndex = Math.floor(sortedNoteheads.length / 2);
            const highlightColor = this.getHighlightColorWithContext(firstNote, highlightContext);
            
            let allVoicesShown = true;
            sortedNoteheads.forEach((notehead, sortedIndex) => {
              // Upper voice = smaller Y values (higher pitches) = first half
              const isUpperVoice = sortedIndex < midIndex || (sortedNoteheads.length === 2 && sortedIndex === 0);

              // Lower voice = larger Y values (lower pitches) = second half
              const isLowerVoice = sortedIndex >= midIndex || (sortedNoteheads.length === 2 && sortedIndex === 1);

              let shouldShow = false;
              if (isUpperVoice && partControl.upperVoice !== false) {
                shouldShow = true;
              } else if (isLowerVoice && partControl.lowerVoice !== false) {
                shouldShow = true;
              }

              // Always keep noteheads visible
              notehead.element.style.display = '';

              if (shouldShow) {
                // Highlight this notehead
                this.applyColorToElements([notehead.element], highlightColor);
              } else {
                allVoicesShown = false;
                // Reset to original colors
                notehead.element.style.fill = '#000000';
                notehead.element.style.stroke = '#000000';
                notehead.element.style.filter = 'none';
                notehead.element.style.strokeWidth = '';
              }
            });

            // The stem is shared by every notehead on it, so only color it
            // when all of them are highlighted
            if (allVoicesShown) {
              this.applyColorToElements(stemElements, highlightColor);
            }
          } else {
            // No part control - highlight all noteheads and their shared stem
            const highlightColor = this.getHighlightColorWithContext(firstNote, highlightContext);
            this.applyColorToElements([...noteheadElements.map(nh => nh.element), ...stemElements], highlightColor);
          }
        } else {
          // Single notehead - the stem belongs to it alone, so highlight both
          const firstNote = noteEvents[0];
          const highlightColor = this.getHighlightColorWithContext(firstNote, highlightContext);
          this.applyColorToElements([noteheadElements[0]?.element, ...stemElements].filter(Boolean), highlightColor);
        }
        
        this.currentHighlights.add(noteId);
        if (highlightContext === 'lookahead') {
          this.lookaheadHighlights.add(noteId);
        }
        return true;
        
      } catch (error) {
        console.error('Error highlighting chord with context:', error);
        return false;
      }
    }
  
    // Store original colors of note elements
    storeOriginalColors(svgElement, noteId) {
      const originalColors = {
        elements: []
      };
  
      // Find all child elements and store their colors
      const allElements = svgElement.querySelectorAll('*');
      
      allElements.forEach((element) => {
        originalColors.elements.push({
          element: element,
          originalFill: element.style.fill || element.getAttribute('fill') || '#000000',
          originalStroke: element.style.stroke || element.getAttribute('stroke') || '#000000'
        });
      });
  
      this.highlightedNotes.set(noteId, originalColors);
    }
  
    // Apply color to specific elements
    applyColorToElements(elements, highlightType) {
      if (highlightType === 'playing') {
        // Red for playing notes (no glow)
        elements.forEach(element => {
          element.style.fill = '#FF0000';
          element.style.stroke = '#FF0000';
          element.style.filter = 'none';
          element.style.strokeWidth = '1.5px';
        });
      } else if (highlightType === 'muted') {
        // Blue for muted/zero volume notes
        elements.forEach(element => {
          element.style.fill = '#0066FF';
          element.style.stroke = '#0066FF';
          element.style.filter = 'none';
          element.style.strokeWidth = '1.5px';
        });
      } else if (highlightType === 'swung') {
        // Green for swung notes with slight glow to make them stand out
        elements.forEach(element => {
          element.style.fill = '#00AA00';
          element.style.stroke = '#00AA00';
          element.style.filter = 'drop-shadow(0px 0px 2px #00AA00)';
          element.style.strokeWidth = '2px';
        });
      } else if (highlightType === 'lookahead') {
        // Near-black for lookahead notes (invisible to user, detectable by autoscroll)
        elements.forEach(element => {
          element.style.fill = 'rgb(1, 1, 1)';
          element.style.stroke = 'rgb(1, 1, 1)';
          element.style.filter = 'none';
          element.style.strokeWidth = ''; // Don't change stroke width to keep notes same size
        });
      }
    }

    // Apply color to all elements of a note based on highlight type
    applyColor(svgElement, highlightType, noteEvent = null) {
      const allElements = svgElement.querySelectorAll('*');
      
      // VOICE-BASED NOTEHEAD TARGETING: Use the same logic as simple-svg-explorer2
      let elementsToColor = allElements;
      
      if (noteEvent && noteEvent.partId) {
        // Find notehead elements to avoid highlighting dots/accidentals
        const noteheadElements = this.findNoteheads(svgElement);
        const stemElements = this.findStemElements(svgElement);

        if (noteheadElements.length >= 2) {
          // console.log(`🎯 CHORD: ${noteEvent.pitch} - found ${noteheadElements.length} noteheads`);
          
          // Get part controls to check voice settings (similar to simple-svg-explorer2)
          // Find the correct part control using the same mapping logic as shouldIncludeNoteForAudio
          let partControl = null;
          if (this.partToInstrumentMapRef && this.partToInstrumentMapRef.current) {
            const instrumentId = this.partToInstrumentMapRef.current[noteEvent.partId];
            partControl = this.getCurrentPartControls().find(part => part.id.toString() === instrumentId?.toString());
          }
          
          // Fallback: try direct match
          if (!partControl) {
            partControl = this.getCurrentPartControls().find(part => part.id.toString() === noteEvent.partId.toString());
          }
          
          if (partControl) {
            // console.log(`🎛️ PART CONTROL for ${noteEvent.pitch}:`, {
            //   upperVoice: partControl.upperVoice,
            //   lowerVoice: partControl.lowerVoice,
            //   partId: noteEvent.partId,
            //   instrumentId: this.partToInstrumentMapRef.current[noteEvent.partId]
            // });
            
            // Sort noteheads by Y position (same as simple-svg-explorer2)
            const sortedNoteheads = [...noteheadElements].sort((a, b) => a.y - b.y);
            // console.log(`🎯 Noteheads Y: ${sortedNoteheads.map(nh => nh.y.toFixed(1)).join(', ')}`);
            
            // Apply the same voice logic as simple-svg-explorer2
            const midIndex = Math.floor(sortedNoteheads.length / 2);
            const targetNoteheads = [];
            
            sortedNoteheads.forEach((notehead, sortedIndex) => {
              let shouldHighlight = false;
              
              // Upper voice = smaller Y values (higher pitches) = first half
              const isUpperVoice = sortedIndex < midIndex || (sortedNoteheads.length === 2 && sortedIndex === 0);
              
              // Lower voice = larger Y values (lower pitches) = second half  
              const isLowerVoice = sortedIndex >= midIndex || (sortedNoteheads.length === 2 && sortedIndex === 1);
              
              if (isUpperVoice && partControl.upperVoice !== false) {
                shouldHighlight = true;
                // console.log(`🎯 Upper voice notehead (Y=${notehead.y.toFixed(1)}) - upperVoice=${partControl.upperVoice} - HIGHLIGHTING`);
              } else if (isLowerVoice && partControl.lowerVoice !== false) {
                shouldHighlight = true;
                // console.log(`🎯 Lower voice notehead (Y=${notehead.y.toFixed(1)}) - lowerVoice=${partControl.lowerVoice} - HIGHLIGHTING`);
              } else {
                // console.log(`🎯 Notehead (Y=${notehead.y.toFixed(1)}) - isUpper=${isUpperVoice}, isLower=${isLowerVoice}, upperVoice=${partControl.upperVoice}, lowerVoice=${partControl.lowerVoice} - SKIPPING`);
              }
              
              if (shouldHighlight) {
                targetNoteheads.push(notehead.element);
              }
            });
            
            if (targetNoteheads.length > 0) {
              // Include the shared stem only when every notehead on it is highlighted
              elementsToColor =
                targetNoteheads.length === sortedNoteheads.length
                  ? [...targetNoteheads, ...stemElements]
                  : targetNoteheads;
              // console.log(`✅ Selected ${targetNoteheads.length} noteheads for ${noteEvent.pitch} based on voice settings`);
            } else {
              // No noteheads should be highlighted based on voice settings
              elementsToColor = [];
              // console.log(`⏭️ No noteheads to highlight for ${noteEvent.pitch} (all voices disabled)`);
            }
          } else {
            // No part control found - highlight all noteheads and their shared stem
            elementsToColor = [...noteheadElements.map(nh => nh.element), ...stemElements];
            // console.log(`⚠️ No part control found for ${noteEvent.pitch}, highlighting all noteheads`);
          }
        } else if (noteheadElements.length === 1) {
          // Single notehead - the stem belongs to it alone, so highlight both
          elementsToColor = [noteheadElements[0].element, ...stemElements];
        } else {
          // No noteheads detected - fallback
          // console.log(`🎯 No noteheads detected for ${noteEvent.pitch}, using all elements`);
        }
      }
      
      if (highlightType === 'playing') {
        // Red for playing notes (no glow)
        elementsToColor.forEach(element => {
          element.style.fill = '#FF0000';
          element.style.stroke = '#FF0000';
          element.style.filter = 'none';
          element.style.strokeWidth = '1.5px';
        });
      } else if (highlightType === 'muted') {
        // Blue for muted/zero volume notes
        elementsToColor.forEach(element => {
          element.style.fill = '#0066FF';
          element.style.stroke = '#0066FF';
          element.style.filter = 'none';
          element.style.strokeWidth = '1.5px';
        });
      } else if (highlightType === 'swung') {
        // Green for swung notes with slight glow to make them stand out
        elementsToColor.forEach(element => {
          element.style.fill = '#00AA00';
          element.style.stroke = '#00AA00';
          element.style.filter = 'drop-shadow(0px 0px 2px #00AA00)';
          element.style.strokeWidth = '2px';
        });
      } else if (highlightType === 'lookahead') {
        // Near-black for lookahead notes (invisible to user, detectable by autoscroll)
        elementsToColor.forEach(element => {
          element.style.fill = 'rgb(1, 1, 1)';
          element.style.stroke = 'rgb(1, 1, 1)';
          element.style.filter = 'none';
          element.style.strokeWidth = ''; // Don't change stroke width to keep notes same size
        });
      }
    }

    // Find notehead elements (same logic as SVG explorer)
    findNoteheads(svgElement) {
      const noteheads = [];
      
      // Look for elements that are likely noteheads
      const candidates = svgElement.querySelectorAll('path, circle, ellipse');
      
      candidates.forEach((element, index) => {
        const bbox = element.getBBox();
        
        // Filter for notehead-like elements (reasonable size, not too thin/wide)
        if (bbox.width > 2 && bbox.height > 2 && bbox.width < 50 && bbox.height < 50) {
          // Check if it looks like a notehead (reasonable aspect ratio)
          const aspectRatio = bbox.width / bbox.height;
          if (aspectRatio > 0.5 && aspectRatio < 3) {
            
            // EXCLUDE DOTS: Check if this might be a dot (very small and round)
            const isDot = bbox.width < 6 && bbox.height < 6 && Math.abs(aspectRatio - 1) < 0.3;
            
            // EXCLUDE ACCIDENTALS: Check for very thin elements (likely accidentals)
            const isThin = bbox.width < 4 || bbox.height < 4;
            
            if (!isDot && !isThin) {
              noteheads.push({
                element: element,
                x: Math.round(bbox.x * 10) / 10,
                y: Math.round(bbox.y * 10) / 10,
                width: Math.round(bbox.width * 10) / 10,
                height: Math.round(bbox.height * 10) / 10,
                tagName: element.tagName
              });
            }
          }
        }
      });
      
      return noteheads;
    }

    // Find stem/flag elements of a note group. A stem is shared by every
    // notehead attached to it, so callers must only color these when ALL of
    // the group's noteheads are being highlighted.
    findStemElements(svgElement) {
      return [...svgElement.querySelectorAll('.vf-stem, .vf-stem *, .vf-flag, .vf-flag *')];
    }

    // Legacy method for backward compatibility
    applyRedColor(svgElement) {
      this.applyColor(svgElement, 'playing');
    }
  
    // Remove highlight from a graphical note
    unhighlightNote(graphicalNote) {
      if (!graphicalNote) return false;
      
      try {
        const noteId = graphicalNote.getSVGId();
        if (!noteId || !this.currentHighlights.has(noteId)) return false;
        
        const originalColors = this.highlightedNotes.get(noteId);
        
        if (originalColors) {
          // Restore original colors and clear any special styling
          originalColors.elements.forEach(elementData => {
            elementData.element.style.fill = elementData.originalFill;
            elementData.element.style.stroke = elementData.originalStroke;
            elementData.element.style.filter = 'none';
            elementData.element.style.strokeWidth = '';
          });
          
          this.currentHighlights.delete(noteId);
        }
        return true;
      } catch (error) {
        return false;
      }
    }
  
    // Force complete highlighting refresh (for voice setting changes)
    forceRefresh() {
      this.clearAllHighlights();
      this.lastHighlightedNotes = ''; // Reset tracking
    }

    // Clear all highlights
    clearAllHighlights() {
      // First try the normal clearing method
      for (const noteId of this.currentHighlights) {
        try {
          const originalColors = this.highlightedNotes.get(noteId);
          if (originalColors) {
            originalColors.elements.forEach(elementData => {
              // Check if element still exists in DOM before trying to modify it
              if (elementData.element && elementData.element.isConnected) {
                elementData.element.style.fill = elementData.originalFill;
                elementData.element.style.stroke = elementData.originalStroke;
                elementData.element.style.filter = 'none';
                elementData.element.style.strokeWidth = '';
              }
            });
          }
        } catch (error) {
          // Silently handle errors when DOM elements are no longer available
        }
      }
      
      // AGGRESSIVE CLEARING: Find and reset all colored elements in the score
      if (this.osmd && this.osmd.container) {
        // Clear red, blue, green, and lookahead highlighted elements
        const coloredElements = this.osmd.container.querySelectorAll(`
          *[style*="fill: rgb(255, 0, 0)"], *[style*="fill: #FF0000"],
          *[style*="stroke: rgb(255, 0, 0)"], *[style*="stroke: #FF0000"],
          *[style*="fill: rgb(0, 102, 255)"], *[style*="fill: #0066FF"],
          *[style*="stroke: rgb(0, 102, 255)"], *[style*="stroke: #0066FF"],
          *[style*="fill: rgb(0, 170, 0)"], *[style*="fill: #00AA00"],
          *[style*="stroke: rgb(0, 170, 0)"], *[style*="stroke: #00AA00"],
          *[style*="fill: rgb(1, 1, 1)"], *[style*="stroke: rgb(1, 1, 1)"]
        `.replace(/\s+/g, ' ').trim());
        
        coloredElements.forEach(element => {
          element.style.fill = '#000000';
          element.style.stroke = '#000000';
          element.style.filter = 'none';
          element.style.strokeWidth = '';
        });
      }
      
      this.currentHighlights.clear();
      this.lookaheadHighlights.clear();
      this.highlightedNotes.clear(); // Also clear the stored colors to force refresh
    }
  
    // Update highlights based on active note events
    updateHighlightsFromActiveNotes(activeNoteEvents) {
      // Check if notes actually changed to reduce spam
      const currentNotesString = activeNoteEvents.map(n => n.pitch).join(',');
      const notesChanged = currentNotesString !== this.lastHighlightedNotes;
      this.lastHighlightedNotes = currentNotesString;
      
      // Clear all current highlights
      this.clearAllHighlights();
      
      
      // Group active notes by SVG ID to handle chords properly
      const notesBySvgId = new Map();
      
      activeNoteEvents.forEach((noteEvent) => {
        if (noteEvent.sourceNote) {
          // Find the corresponding graphical note
          const graphicalNote = this.findGraphicalNoteForSourceNote(noteEvent.sourceNote);
          if (graphicalNote) {
            const svgElement = graphicalNote.getSVGGElement();
            const svgId = svgElement?.id;
            
            if (svgId) {
              if (!notesBySvgId.has(svgId)) {
                notesBySvgId.set(svgId, []);
              }
              notesBySvgId.get(svgId).push({
                noteEvent,
                graphicalNote,
                svgElement
              });
            }
          }
        }
      });
      
      // Process each SVG group
      notesBySvgId.forEach((notesInGroup, svgId) => {
        if (notesInGroup.length === 1) {
          // Single note - highlight normally
          const { noteEvent, graphicalNote } = notesInGroup[0];
          this.highlightNote(graphicalNote, noteEvent);
        } else {
          // Multiple notes in same SVG group (chord) - highlight as a group with voice filtering
          
          // Use the first note's graphical context but pass all note events for voice filtering
          const { graphicalNote, svgElement } = notesInGroup[0];
          this.highlightChord(graphicalNote, svgElement, notesInGroup.map(n => n.noteEvent));
          
          if (notesChanged) {
            // console.log(`🎯 Chord processed with ${notesInGroup.length} notes`);
          }
        }
      });
    }

    // Update highlights with both current notes and lookahead notes
    updateHighlightsWithLookahead(activeNoteEvents, lookaheadNoteEvents) {
      // Check if notes actually changed to reduce spam
      const currentNotesString = activeNoteEvents.map(n => n.pitch).join(',');
      const lookaheadNotesString = lookaheadNoteEvents.map(n => n.pitch).join(',');
      const combinedNotesString = currentNotesString + '|' + lookaheadNotesString;
      const notesChanged = combinedNotesString !== this.lastHighlightedNotes;
      this.lastHighlightedNotes = combinedNotesString;
      
      // Clear all current highlights
      this.clearAllHighlights();
      
      // Process current (playing) notes first
      this.processNotesWithHighlightType(activeNoteEvents, 'current');
      
      // Process lookahead notes with yellow highlighting
      this.processNotesWithHighlightType(lookaheadNoteEvents, 'lookahead');
    }

    // Helper method to process notes with a specific highlight type
    processNotesWithHighlightType(noteEvents, highlightContext) {
      // Group notes by SVG ID to handle chords properly
      const notesBySvgId = new Map();
      
      noteEvents.forEach((noteEvent) => {
        if (noteEvent.sourceNote) {
          // Find the corresponding graphical note
          const graphicalNote = this.findGraphicalNoteForSourceNote(noteEvent.sourceNote);
          if (graphicalNote) {
            const svgElement = graphicalNote.getSVGGElement();
            const svgId = svgElement?.id;
            
            if (svgId) {
              if (!notesBySvgId.has(svgId)) {
                notesBySvgId.set(svgId, []);
              }
              notesBySvgId.get(svgId).push({
                noteEvent,
                graphicalNote,
                svgElement,
                highlightContext
              });
            }
          }
        }
      });
      
      // Process each SVG group
      notesBySvgId.forEach((notesInGroup, svgId) => {
        // Skip if this SVG group is already highlighted (current notes take precedence)
        if (this.currentHighlights.has(svgId) && highlightContext === 'lookahead') {
          return;
        }
        
        if (notesInGroup.length === 1) {
          // Single note
          const { noteEvent, graphicalNote, highlightContext } = notesInGroup[0];
          this.highlightNoteWithContext(graphicalNote, noteEvent, highlightContext);
        } else {
          // Multiple notes in same SVG group (chord)
          const { graphicalNote, svgElement, highlightContext } = notesInGroup[0];
          this.highlightChordWithContext(graphicalNote, svgElement, notesInGroup.map(n => n.noteEvent), highlightContext);
        }
      });
    }

  
    // Get currently highlighted note IDs (for autoscroll)
    getCurrentHighlights() {
      return this.currentHighlights;
    }

    // Get currently highlighted lookahead note IDs (for autoscroll)
    getLookaheadHighlights() {
      return this.lookaheadHighlights;
    }
  
    // Get count of currently highlighted notes
    getHighlightedCount() {
      return this.currentHighlights.size;
    }

    // Reset internal state after DOM structure changes (e.g., after part visibility changes)
    resetAfterDOMChange() {
      // Clear all tracking without trying to restore colors
      // since DOM elements may have changed
      this.highlightedNotes.clear();
      this.currentHighlights.clear();
    }
  }